import {Overlay, OverlayPositionBuilder} from '@angular/cdk/overlay';
import { OverlayRef } from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {ElementRef, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MeatBallMenuComponent} from './meatball-menu.component';

import {ProductServiceResponse, RequestRequiredData} from './meatball-menu.model';
import {environment} from '../../../../environments/environment';
import {ProductOverviewRequest} from '../../product-overview/product-overview-request.model';
import {URL_BOC} from "./meatball-menu.constants";

const baseUrl = environment.productViewDetailsBaseURL;

export interface MeatBallMenu {
  title: string;
  productServiceList: ProductServiceResponse;
  requestRequiredData: RequestRequiredData;
  otherLinks: [];
  hasBOCockpit: boolean;
  hasMijnWonen: boolean;
  hasGeldOver: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MeatballMenuService {
  public meatBallMenuItem: [ElementRef, MeatBallMenu];
  public hasMeatballMenuOpened = false;
  public currentMeatBallMenuRef: OverlayRef | undefined;
  public requiredRequestData: RequestRequiredData | undefined;
  private bockUrl: string | undefined;
  private readonly civServicesBaseUrl: string;

  constructor(
    public readonly overlay: Overlay,
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly http: HttpClient,
  ) {
    this.civServicesBaseUrl = environment.civServicesBaseURL;
  }

  public addInfoToMeatBallMenu(elementRef: ElementRef, data: MeatBallMenu) {
    this.meatBallMenuItem = [elementRef, data];
    this.requiredRequestData = data.requestRequiredData;
    this.getProductServices().subscribe((productService: ProductServiceResponse) => {
      if (!productService) {
        return;
      }
      this.meatBallMenuItem[1].productServiceList = productService;
      this.showMeatBallMenu();
    });
  }

  public showMeatBallMenu(): void {
    if (this.hasMeatballMenuOpened) {
      return;
    }

    const helperData = this.meatBallMenuItem[1];

    if (helperData) {
      this.currentMeatBallMenuRef = this.createMeatballMenuOverlay(this.meatBallMenuItem[0]);
      const helperPortal = this.currentMeatBallMenuRef.attach(new ComponentPortal(MeatBallMenuComponent));
      helperPortal.instance.title = helperData.title;
      helperPortal.instance.productServiceList = helperData.productServiceList;
      helperPortal.instance.otherLinks = helperData.otherLinks;
      helperPortal.instance.hasBOCockpit = helperData.hasBOCockpit;
      helperPortal.instance.hasMijnWonen = helperData.hasMijnWonen;
      helperPortal.instance.hasGeldOver = helperData.hasGeldOver;

      this.hasMeatballMenuOpened = true;
    }
  }

  public hideMeatBallMenu(): void {
    if (!this.hasMeatballMenuOpened) {
      return;
    }

    if (this.currentMeatBallMenuRef && this.currentMeatBallMenuRef.hasAttached()) {
      this.currentMeatBallMenuRef.detach();
      this.currentMeatBallMenuRef.dispose();
    }

    this.hasMeatballMenuOpened = false;
  }

  public createMeatballMenuOverlay(elementRef: ElementRef): OverlayRef {
    return this.overlay.create({
      positionStrategy: this.overlayPositionBuilder
        .flexibleConnectedTo(elementRef)
        .withDefaultOffsetY(0)
        .withPositions([{
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        }, {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        }])
        .withPush(false),
    });
  }

  public hideOverlay(): void {
    if (this.currentMeatBallMenuRef) {
      this.hideMeatBallMenu();
    }
  }

  public getProductServices(): Observable<ProductServiceResponse> {
    const productServiceUrl = `${baseUrl}productservices`;
    const headers = this.createHeaders();

    return this.http.post<ProductServiceResponse>(productServiceUrl, this.requiredRequestData, { headers });
  }

  public getCockPitUrl() {
    const buttonUrlRequestBody = {
      url: 'boc-url',
      arrangementNumber: this.requiredRequestData.arrangementNumber,
      administrationCode: this.requiredRequestData.administrationCode,
      commercialProductTypeCode: this.requiredRequestData.commercialProductTypeCode,
      parameterToken: ProductOverviewRequest.token || '',
    };

    return this.getButtonUrl(buttonUrlRequestBody).subscribe(
      (res) => {
        if (res.listOfError && res.listOfError.length) {
          window.open(URL_BOC, '_blank');
          return;
        }
        this.redirectToUrl(res);
      },
      () => {
        if (this.bockUrl === 'boc-url') {
          window.open(URL_BOC, '_blank');
        }
      },
    );
  }

  getButtonUrl(data) {
    const buttonURL = this.civServicesBaseUrl + data.url;
    const {url: _, ...requestBody} = data; // removes the url param from the object
    const headers = this.createHeaders();
    return this.http.post<any>(buttonURL, JSON.stringify(requestBody), {headers});
  }

  redirectToUrl(res) {
    const url = res.psUrl ? res.psURL : res.url;
    const decodedUrl = atob(url);
    window.open(decodedUrl, '_blank');
  }

  private createHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append('Authorization', ProductOverviewRequest.token);
    headers = headers.append('MFA-Authorization', ProductOverviewRequest.token);
    headers = headers.append('Graph-Authorization', ProductOverviewRequest.graphToken);
    headers = headers.append('Content-type', 'application/json');
    return headers;
  }


  getWonenPortaalURL() {
    const headers = this.createHeaders();
    const wonenPortaalUrlRequestBody = {
      parametersToken: this.requiredRequestData.parametersToken || '',
    };
    return this.http.post(this.civServicesBaseUrl + 'wonenportaal', JSON.stringify(wonenPortaalUrlRequestBody), {headers});
  }

  public getGeldOverPortalURL() {
    const getRelationIdUrl = this.civServicesBaseUrl  + 'getRelationId';
    const headers = this.createHeaders();
    const geldOverPortalURLRequestBody = {
      parametersToken: this.requiredRequestData.parametersToken || '',
    };

    return this.http.post(getRelationIdUrl, JSON.stringify(geldOverPortalURLRequestBody), {headers});
  }
}
